import type { MediaSize } from './types';
import type { RenderFlags } from './frameByFrameRenderer/Renderer';
import { FrameByFrameRenderer } from './frameByFrameRenderer/Renderer';
import { VideoFrameExtractor } from './frameByFrameRenderer/frameExtractors/videoFrameExtractor';
import type { OpaqueVideoData } from './video-data/types';
import { OfflineAudioEncoder } from './encoders/OfflineAudioEncoder';
import { VideoWebcodecsBufferRecorder } from './videoWebCodecsRecorder';

type RenderSlideOptions = {
    resolutionValue: number | null;
    mediaSize?: MediaSize;
    startTime: number;
    endTime: number;
    videoData: OpaqueVideoData;
    flags: RenderFlags;
    /**
     * allows to show the rendering process in the nwjs window for debugging purposes
     */
    canvasParent?: HTMLElement;
};

/**
 * Render a slice of the given video data to mp4 buffer
 */
export const renderMp4 = async ({
    videoData,
    startTime,
    endTime,
    resolutionValue,
    mediaSize,
    flags,
    canvasParent,
}: RenderSlideOptions): Promise<ArrayBuffer> => {
    const audioEncoder = new OfflineAudioEncoder({ flags });
    audioEncoder.setStartTime(startTime);
    audioEncoder.setStopTime(endTime);
    await audioEncoder.encode(videoData);

    const recorder = new VideoWebcodecsBufferRecorder({
        audioBuffer: audioEncoder.audioBuffer!,
    });

    audioEncoder.destroy();

    const frameExtractor = new VideoFrameExtractor();

    const renderer = new FrameByFrameRenderer({
        videoData,
        startTime,
        endTime,
        resolutionValue,
        mediaSize,
        pipeline: { recorder, frameExtractor },
        flags,
        canvasParent,
    });

    await renderer.render();

    return recorder.mp4Buffer;
};
